import { useEffect, useState, MouseEvent } from "react"
import ConditionalSelectField from "./ConditionSelectField";
import SelectField from "./SelectFields";
import { useGetTagsTagsGet } from "../../api/generated/fastAPI";
import { ReactComponent as ClearIcon } from '../../assets/Icons/adornments/Close.svg';

export default function TagsField({
    label,
    tags,
    showSelect,
    handleAddTag,
    handleRemoveTag,
    menuPortalTarget,
}: {
    label: string,
    tags: string[],
    showSelect: boolean,
    handleAddTag: ( category: string, tag: string ) => void,
    handleRemoveTag: ( index: number ) => void,
    menuPortalTarget?: HTMLElement | null,
}) {
    const getTagsMutation = useGetTagsTagsGet();

    const [ allTags, setAllTags ] = useState<{ [key: string]: string[] } | null>(null);
    const [ categoryOptions, setCategoryOptions ] = useState<{ value: string | number, label: string | number }[]>([]);
    const [ tagOptions, setTagOptions ] = useState<{ value: string | number, label: string | number }[]>([]);

    const [ selectedCategory, setSelectedCategory ] = useState('general');
    const [ selectedTag, setSelectedTag ] = useState('');

    useEffect(() => {
        if (getTagsMutation.data) {
            const tagsData = getTagsMutation.data as unknown as { [key: string]: string[] };
            setAllTags(tagsData);

            let categories: { value: string | number, label: string | number }[] = [];

            Object.keys(tagsData).forEach(key => {
                if (key === 'general') {
                    categories.push({ value: key, label: 'Uncategorized' });
                } else {
                    categories.push({ value: key, label: key })
                }
            });

            setCategoryOptions(categories);
        }
    }, [getTagsMutation.data])

    useEffect(() => {
        if (allTags && selectedCategory) {
            const matchingCategory = Object.keys(allTags).find(key => key === selectedCategory);
            if (matchingCategory) {
                const matchingTags = allTags[matchingCategory];
                setTagOptions(matchingTags.map(tag => ({ value: tag, label: tag })))
            } else {
                setTagOptions([]);
            }
        }
    }, [allTags, selectedCategory]);

    

    const handleSelect = (e: {value: string | number, label: string | number} | {value: string | number, label: string | number}[], name: string) => {
        const value = Array.isArray(e) ? e.map(val => val.value) : e.value;
        
        if (name === 'category') {
            setSelectedCategory(value as string);
            setSelectedTag('');
        } else {
            setSelectedTag(value as string);
        }
    }

    const addTag = (e: MouseEvent<HTMLButtonElement>, category: string, tag: string) => {
        e.preventDefault();
        handleAddTag(category, tag);
        setSelectedTag('');
    }

    const removeTag = (e: MouseEvent<HTMLButtonElement>, index: number) => {
        e.preventDefault();
        handleRemoveTag(index);
    }

    return (
        <div className="flex flex-col justify-start items-start gap-6 w-full">
            <div className="flex flex-col justify-start items-start gap-1">
                <p className="text-default font-medium">{label} Tags <span className="text-disable">({tags.length})</span></p>
                <p className="text-[10px] text-disable">Click the Add button to add a tag</p>
            </div>

            <div className="flex flex-col justify-start items-start gap-3 w-full">
                <div className="w-full flex justify-start items-end gap-4">
                    <div className="w-1/2">
                        <ConditionalSelectField label="Category" showSelect={showSelect}>
                            <SelectField 
                                label="Category"
                                id='category'
                                name='category'
                                options={categoryOptions}
                                value={categoryOptions.length > 0 ? categoryOptions.find(opt => opt.value === selectedCategory)! : null}
                                isRequired={false}
                                handleChange={handleSelect}
                                isCreatable
                                isMulti={false}
                                menuPortalTarget={menuPortalTarget}
                            />
                        </ConditionalSelectField>
                    </div>
                    <div className="w-full grow">
                        <ConditionalSelectField label="Tag" showSelect={showSelect}>
                            <SelectField 
                                label="Tag"
                                id='tag'
                                name='tag'
                                options={tagOptions}
                                value={selectedTag ? { value: selectedTag, label: selectedTag } : null}
                                isRequired={false}
                                handleChange={handleSelect}
                                isCreatable
                                isMulti={false}
                                menuPortalTarget={menuPortalTarget}
                            />
                        </ConditionalSelectField>
                    </div>
                    <button 
                        disabled={!selectedCategory || !selectedTag}
                        onClick={(e:any) => addTag(e, selectedCategory, selectedTag)} 
                        className={`flex justify-between items-center gap-4 px-1 py-3 bg-none font-medium text-highlight hover:text-primary transition-all `}
                    >
                        Add
                    </button>
                </div>
                <div className="w-full p-2 border border-outline rounded-lg h-[100px]">
                {tags.length > 0 ? (
                    <div className="w-full flex flex-wrap justify-start items-start gap-2 overflow-y-auto h-full">
                        {tags.map((tag, i) => (
                            <div key={i} className="py-2 px-4 rounded-lg bg-secondary flex justify-start items-center gap-1">
                                <p className="font-semibold text-primary text-[12px]">{tag}</p>
                                <button onClick={(e:any) => removeTag(e, i)} className="w-[18px] text-disable hover:text-default transition-all"><ClearIcon/></button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="w-full h-full flex justify-center items-center">
                        <p className="text-disable">No tags selected</p>
                    </div>
                )}
                </div>
            </div>

        </div>
    )
}